import { inject } from '@angular/core';
import { HttpErrorResponse, type HttpEvent, type HttpHandlerFn, type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { FeatureAccessClientService } from './feature-access-client.service';
import { matchAccessRegistryEntry } from './access-registry';

/**
 * Short-circuits outbound requests to endpoints the client already knows are
 * not accessible (licence not covering the feature OR user's role missing the
 * permission — the intersection is server-computed via `effectivePermissions`
 * on GET /api/v1/features and consumed by
 * {@link FeatureAccessClientService.hasPermission}).
 *
 * Fires a synthetic `HttpErrorResponse` with status 403 + code
 * `CLIENT_ACCESS_DENIED` — same shape a real server rejection would take, so
 * downstream error handlers keep working.
 *
 * Fail-open: if the features resource has not resolved yet, the request
 * proceeds normally. The server-side FeatureGuard remains the authoritative
 * enforcement.
 *
 * Foundation endpoints (auth, /api/v1/users/me, /api/v1/features, RBAC, audit,
 * settings, tenants, environments) are intentionally NOT in the registry —
 * they must always fire so the app can bootstrap.
 */
export const accessInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const featureAccess = inject(FeatureAccessClientService);

  const entry = matchAccessRegistryEntry(extractPath(req.url), req.method);
  const featureAnyOf = entry?.featureAnyOf;
  const feature = entry?.feature;
  const permission = entry?.permission;
  if (featureAccess.featuresLoaded() && featureAnyOf !== undefined && !featureAnyOf.some(f => featureAccess.hasFeature(f))) {
    const reason = `none of the features '${featureAnyOf.join(', ')}' active`;
    return throwError(() => buildDenied(req.url, reason));
  }
  if (featureAccess.featuresLoaded() && featureAnyOf === undefined && feature !== undefined && !featureAccess.hasFeature(feature)) {
    const reason = `feature '${feature}' not active`;
    return throwError(() => buildDenied(req.url, reason));
  }
  if (featureAccess.featuresLoaded() && permission !== undefined && !featureAccess.hasPermission(permission)) {
    const reason = `permission '${permission}' missing or feature-gated`;
    return throwError(() => buildDenied(req.url, reason));
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (isFeatureNotLicensed(error)) featureAccess.refresh();
      return throwError(() => error);
    })
  );
};

function isFeatureNotLicensed(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.error as { code?: string } | null)?.code === 'FEATURE_NOT_LICENSED';
}

function extractPath(url: string): string {
  const idx = url.indexOf('/api/');
  return idx >= 0 ? url.slice(idx) : url;
}

function buildDenied(url: string, reason: string): HttpErrorResponse {
  return new HttpErrorResponse({
    status: 403,
    statusText: 'Forbidden',
    url,
    error: {
      code: 'CLIENT_ACCESS_DENIED',
      message: `Client-side access-registry blocked request: ${reason}`,
    },
  });
}
